// Diagnose Mode: Standalone (Mode 1), VDR-invoked (Mode 2), and VDR multi-session (Mode 3). Chrome trims itself in Mode 2/3 via isVDRSession.
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { Menu } from 'primeng/menu';
import { filter, map, Subject, takeUntil } from 'rxjs';
import { DialogCommonService } from '../../../../services/common/dialog-common.service';
import { Mode3ContextService } from '../../../../services/mode3-context.service';
import { RootState } from '../../../../store/reducers/RootState.model';
import { PSLActions } from '../../../../store/workflow/actions/user-profile.actions';

import { SessionActions } from '../../../../store/workflow/actions/workflow.actions';
import { selectPSL } from '../../../../store/workflow/selectors/user-profile.selectors';
import {
  selectDiagnosisId,
  selectRelatedVDRJob,
  selectSessionStatus,
} from '../../../../store/workflow/selectors/workflow.selectors';
import { CURRENT_STATUS } from '../../../constants/status.constant';
import { PSL } from '../../../models/master-data/psl';

@Component({
  selector: 'app-workflow-header',
  imports: [AvatarModule, AvatarGroupModule, Menu, AsyncPipe],
  templateUrl: './header-v2.component.html',
  styleUrl: './header-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderV2Component implements OnInit, OnDestroy {
  #store = inject(Store<RootState>);
  private destroy$ = new Subject<void>();

  #dialogCommonService = inject(DialogCommonService);
  readonly #router = inject(Router);
  readonly #authService = inject(MsalService);
  readonly #mode3Context = inject(Mode3ContextService);

  selectedPSL = signal<PSL | null>(null);
  diagnosisId = signal<string>('');
  vdrJob$ = this.#store.select(selectRelatedVDRJob);
  isVDRSession = toSignal(this.#store.select(selectRelatedVDRJob));
  sessionStatus = toSignal(this.#store.select(selectSessionStatus));

  // Mode 3 header block — shown when Mode3ContextService.projectId() is set. Prefers the
  // Mobius-facing referenceId (populated once GetProjectSessions returns); falls back to the
  // internal Guid so the block appears immediately on landing while data is still loading.
  mode3ProjectId = this.#mode3Context.projectId;
  mode3ReferenceId = this.#mode3Context.referenceId;
  mode3DisplayId = computed(() => this.mode3ReferenceId() ?? this.mode3ProjectId());

  @Input() labelAvatar = '';

  items: Signal<MenuItem[]> = computed(() => [
    {
      label: this.selectedPSL()
        ? `Selected PSL: ${this.selectedPSL()?.name}`
        : 'PSL Selection',
      disabled:
        !!this.isVDRSession() ||
        !!this.#mode3Context.projectId() ||
        (this.sessionStatus() !== '' &&
          this.sessionStatus() !== CURRENT_STATUS.IN_PROGRESS),
      command: () => {
        this.openPSLSelection(this.selectedPSL()?.id);
      },
    },
    {
      label: 'Admin Page',
      styleClass: '',
      disabled: !!this.isVDRSession(),
      command: () => {
        this.#router.navigate(['/admin']);
      },
    },
    {
      label: 'UoM Settings',
      command: () => {
        this.openUserSetting();
      },
    },
    {
      label: 'Log out',
      styleClass: 'log-out',
      command: () => {
        this.logOut();
      },
    },
  ]);

  ngOnInit() {
    this.#store.dispatch(PSLActions.getCurrentPSL());

    this.loadData();
  }

  private loadData() {
    this.loadSessionData();
    this.loadCurrentPSL();
  }

  private loadCurrentPSL() {
    this.#store
      .select(selectPSL)
      .pipe(
        filter((psl) => !!psl),
        takeUntil(this.destroy$),
      )
      .subscribe((psl) => {
        if (psl.id) {
          this.selectedPSL.set(psl);
        } else if (!this.#isMode3Route()) {
          // Mode 3 owns PSL selection via its own modal; suppress the legacy auto-prompt.
          this.openPSLSelection();
        }
      });
  }

  // URL-based because the lazy Mode3ProjectComponent (which populates Mode3ContextService)
  // hasn't mounted yet when this header runs its first PSL-selector callback.
  #isMode3Route(): boolean {
    return (
      this.#router.url.includes('/pid/') || !!this.#mode3Context.projectId()
    );
  }

  private loadSessionData() {
    this.#store
      .select(selectDiagnosisId)
      .pipe(
        filter((diagnosisId) => !!diagnosisId),
        takeUntil(this.destroy$),
      )
      .subscribe((diagnosisId) => {
        this.diagnosisId.set(diagnosisId);
      });
  }

  openPSLSelection(previousPSLId?: string) {
    this.#dialogCommonService
      .openPSLSelection()
      .pipe(takeUntil(this.destroy$))
      .subscribe((psl: PSL) => {
        if (psl && psl.id) {
          if (this.selectedPSL()?.id !== psl.id) {
            if (previousPSLId && this.diagnosisId()) {
              this.#store.dispatch(
                SessionActions.resetSession({
                  diagnosisId: this.diagnosisId(),
                  pslCode: psl.code || '',
                }),
              );
            }
          }

          this.selectedPSL.set(psl);
          this.#store.dispatch(PSLActions.setCurrentPSL({ psl }));
        }
      });
  }

  openUserSetting() {
    this.#dialogCommonService.openUserSetting().subscribe();
  }

  logOut(): void {
    this.#authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
